// src/components/weather/WeatherIcon.js
// Maps NWS weather conditions to beautiful react-icons/wi weather icons

import React from 'react';
import {
  WiDaySunny,
  WiNightClear,
  WiDayCloudy,
  WiNightAltCloudy,
  WiCloud,
  WiCloudy,
  WiRain,
  WiDayRain,
  WiNightAltRain,
  WiShowers,
  WiDayShowers,
  WiNightAltShowers,
  WiThunderstorm,
  WiDayThunderstorm,
  WiNightAltThunderstorm,
  WiSnow,
  WiDaySnow,
  WiNightAltSnow,
  WiSnowWind,
  WiSleet,
  WiRainMix,
  WiFog,
  WiDayFog,
  WiNightFog,
  WiDayHaze,
  WiDust,
  WiSmoke,
  WiStrongWind,
  WiHot,
  WiSnowflakeCold,
  WiTornado,
  WiHurricane,
  WiNa,
} from 'react-icons/wi';

// NWS condition keyword → { day icon, night icon, color }
const CONDITION_MAP = {
  // Clear
  skc:             { day: WiDaySunny,          night: WiNightClear,           color: '#FFD93D' },
  hot:             { day: WiHot,               night: WiHot,                  color: '#FF7043' },
  cold:            { day: WiSnowflakeCold,     night: WiSnowflakeCold,        color: '#81D4FA' },

  // Clouds
  few:             { day: WiDayCloudy,         night: WiNightAltCloudy,       color: '#90CAF9' },
  sct:             { day: WiDayCloudy,         night: WiNightAltCloudy,       color: '#78909C' },
  bkn:             { day: WiCloud,             night: WiCloud,                color: '#78909C' },
  ovc:             { day: WiCloudy,            night: WiCloudy,               color: '#607D8B' },

  // Wind + clouds
  wind_skc:        { day: WiStrongWind,        night: WiStrongWind,           color: '#90A4AE' },
  wind_few:        { day: WiStrongWind,        night: WiStrongWind,           color: '#90A4AE' },
  wind_sct:        { day: WiStrongWind,        night: WiStrongWind,           color: '#90A4AE' },
  wind_bkn:        { day: WiStrongWind,        night: WiStrongWind,           color: '#78909C' },
  wind_ovc:        { day: WiStrongWind,        night: WiStrongWind,           color: '#607D8B' },

  // Rain
  rain:            { day: WiDayRain,           night: WiNightAltRain,         color: '#42A5F5' },
  rain_showers:    { day: WiDayShowers,        night: WiNightAltShowers,      color: '#64B5F6' },
  rain_showers_hi: { day: WiShowers,           night: WiShowers,              color: '#42A5F5' },

  // Thunderstorms
  tsra:            { day: WiDayThunderstorm,   night: WiNightAltThunderstorm, color: '#7C4DFF' },
  tsra_sct:        { day: WiDayThunderstorm,   night: WiNightAltThunderstorm, color: '#7C4DFF' },
  tsra_hi:         { day: WiThunderstorm,      night: WiThunderstorm,         color: '#651FFF' },

  // Snow
  snow:            { day: WiDaySnow,           night: WiNightAltSnow,         color: '#B3E5FC' },
  blizzard:        { day: WiSnowWind,          night: WiSnowWind,             color: '#E1F5FE' },

  // Mixed precipitation
  rain_snow:       { day: WiRainMix,           night: WiRainMix,              color: '#80DEEA' },
  rain_sleet:      { day: WiSleet,             night: WiSleet,                color: '#80CBC4' },
  snow_sleet:      { day: WiSleet,             night: WiSleet,                color: '#B2DFDB' },
  fzra:            { day: WiRainMix,           night: WiRainMix,              color: '#80DEEA' },
  rain_fzra:       { day: WiRainMix,           night: WiRainMix,              color: '#80DEEA' },
  snow_fzra:       { day: WiRainMix,           night: WiRainMix,              color: '#B3E5FC' },
  sleet:           { day: WiSleet,             night: WiSleet,                color: '#80CBC4' },

  // Fog / Haze / Smoke
  fog:             { day: WiDayFog,            night: WiNightFog,             color: '#B0BEC5' },
  haze:            { day: WiDayHaze,           night: WiFog,                  color: '#BCAAA4' },
  smoke:           { day: WiSmoke,             night: WiSmoke,                color: '#A1887F' },
  dust:            { day: WiDust,              night: WiDust,                 color: '#BCAAA4' },

  // Severe
  tornado:         { day: WiTornado,           night: WiTornado,              color: '#EF5350' },
  hurricane:       { day: WiHurricane,         night: WiHurricane,            color: '#EF5350' },
  tropical_storm:  { day: WiHurricane,         night: WiHurricane,            color: '#FF7043' },
};

/**
 * Extract the NWS condition keyword from an icon URL.
 * URL pattern: https://api.weather.gov/icons/land/day/rain,30/rain,20?size=medium
 */
const parseNwsIcon = (iconUrl) => {
  if (!iconUrl) return { condition: null, isNight: false };
  const isNight = iconUrl.includes('/night/');
  const match = iconUrl.match(/\/icons\/land\/(?:day|night)\/([^,?/]+)/);
  const condition = match ? match[1] : null;
  return { condition, isNight };
};

/**
 * Get the icon component and color for an NWS icon URL.
 */
export const getWeatherIcon = (iconUrl) => {
  const { condition, isNight } = parseNwsIcon(iconUrl);
  const entry = condition ? CONDITION_MAP[condition] : null;

  if (!entry) {
    return {
      Icon: isNight ? WiNightClear : WiDaySunny,
      color: '#78909C',
    };
  }

  return {
    Icon: isNight ? entry.night : entry.day,
    color: entry.color,
  };
};

/**
 * WeatherIcon component - renders the appropriate weather icon.
 * @param {string} iconUrl - NWS icon URL
 * @param {number} size - Icon size in pixels (default 48)
 */
const WeatherIconComponent = ({ iconUrl, size = 48, style = {} }) => {
  const { Icon, color } = getWeatherIcon(iconUrl);

  return (
    <Icon
      size={size}
      style={{
        color,
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
        ...style,
      }}
    />
  );
};

export default WeatherIconComponent;
